import {
    SearchFilterDefaultValue,
    SearchFilterDefinition,
    SearchFilterOption,
    SearchFilterSelection,
    SearchFilterValue,
} from 'shared/search-engine';

export interface MathiaFacetItem {
    id?: string | number;
    value?: string | number;
    name?: string;
    label?: string;
    count?: string | number;
    [key: string]: unknown;
}

export type MathiaFacetScope = 'activity' | 'lesson';

export interface MathiaSearchFilterConfig {
    key: string;
    queryParam: string;
    labelKey: string;
    facetKey?: string;
    control?: 'select' | 'checkbox';
    independent?: boolean;
    omitWhenFalse?: boolean;
    multiple?: boolean;
    defaultValue?: SearchFilterDefaultValue<SearchFilterValue>;
}

/**
 * The array order is the display order. The filter bar deliberately does not
 * infer dependencies from the order: the Mathia adapter reloads the facets
 * from the API when a preceding value changes.
 */
export const MATHIA_SEARCH_FILTER_CONFIG: readonly MathiaSearchFilterConfig[] = [
    {
        key: 'level',
        queryParam: 'level',
        labelKey: 'generic.level',
        facetKey: 'levels',
        multiple: true,
    },
    {
        key: 'concepts',
        queryParam: 'concepts',
        labelKey: 'generic.concepts',
        facetKey: 'concepts',
        multiple: true,
    },
    {
        key: 'thematics',
        queryParam: 'thematics',
        labelKey: 'generic.thematics',
        facetKey: 'thematics',
        multiple: true,
    },
    {
        key: 'skills',
        queryParam: 'skills',
        labelKey: 'generic.skills',
        facetKey: 'skills',
        multiple: true,
    },
];

export const MATHIA_PARCOURS_SEARCH_FILTER_CONFIG: readonly MathiaSearchFilterConfig[] = [
    ...MATHIA_SEARCH_FILTER_CONFIG,
    {
        key: 'bookmarks',
        queryParam: 'bookmarks',
        labelKey: 'generic.filter_by_bookmarks',
        control: 'checkbox',
        independent: true,
        omitWhenFalse: true,
    },
];

export type MathiaSearchSelection = SearchFilterSelection<SearchFilterValue>;

export function buildMathiaFilterDefinitions(
    facets: Record<string, unknown>,
    config: readonly MathiaSearchFilterConfig[] = MATHIA_SEARCH_FILTER_CONFIG,
    optionOverrides: Readonly<Record<string, readonly SearchFilterOption<SearchFilterValue>[]>> = {},
    defaultValues: Readonly<Record<string, SearchFilterDefaultValue<SearchFilterValue>>> = {},
): SearchFilterDefinition<SearchFilterValue>[] {
    return config.map(filterConfig => {
        const options = optionOverrides[filterConfig.key]
            ?? (filterConfig.facetKey ? toOptions(facets[filterConfig.facetKey]) : []);

        return {
            key: filterConfig.key,
            queryParam: filterConfig.queryParam,
            labelKey: filterConfig.labelKey,
            options,
            control: filterConfig.control,
            multiple: filterConfig.multiple === true,
            enabled: filterConfig.control === 'checkbox' || options.length > 0,
            defaultValue: resolveDefaultValue(
                defaultValues[filterConfig.key] ?? filterConfig.defaultValue,
                options,
            ),
        } satisfies SearchFilterDefinition<SearchFilterValue>;
    });
}

function resolveDefaultValue(
    rawDefaultValue: SearchFilterDefaultValue<SearchFilterValue> | undefined,
    options: readonly SearchFilterOption<SearchFilterValue>[],
): SearchFilterDefaultValue<SearchFilterValue> | undefined {
    if (rawDefaultValue === undefined || rawDefaultValue === null) {
        return rawDefaultValue;
    }

    if (Array.isArray(rawDefaultValue)) {
        return rawDefaultValue.map(value => resolveDefaultValue(value as SearchFilterDefaultValue<SearchFilterValue>, options) as SearchFilterValue).filter((value): value is SearchFilterValue => value !== undefined && value !== null);
    }

    if (typeof rawDefaultValue !== 'object') {
        const optionMatch = options.find(option => String(option.value) == String(rawDefaultValue)
            || String(option.label) == String(rawDefaultValue)
            || String(option.metadata && typeof option.metadata === 'object'
                ? (option.metadata as Record<string, unknown>).name
                    ?? (option.metadata as Record<string, unknown>).label
                    ?? (option.metadata as Record<string, unknown>).level
                : '') == String(rawDefaultValue));
        return optionMatch?.value ?? rawDefaultValue;
    }

    const candidate = rawDefaultValue as Record<string, unknown>;
    const optionValue = candidate.value;
    if (typeof optionValue === 'string' || typeof optionValue === 'number' || typeof optionValue === 'boolean') {
        return optionValue;
    }

    const optionMatch = options.find(option => {
        const optionId = option.metadata && typeof option.metadata === 'object'
            ? (option.metadata as Record<string, unknown>).id ?? (option.metadata as Record<string, unknown>).value
            : undefined;
        const candidateLabel = candidate.label ?? candidate.name ?? candidate.level ?? candidate.key;
        const candidateId = candidate.id ?? candidateIdFromMetadata(candidate.metadata) ?? candidate.value;

        return String(option.value) === String(candidateId)
            || String(option.label) === String(candidateLabel)
            || String(option.metadata && typeof option.metadata === 'object'
                ? (option.metadata as Record<string, unknown>).name ?? (option.metadata as Record<string, unknown>).label ?? (option.metadata as Record<string, unknown>).level
                : '') === String(candidateLabel)
            || String(optionId) === String(candidateId);
    });

    if (optionMatch) {
        return optionMatch.value;
    }

    const maybeId = candidate.id ?? candidate.value ?? candidateIdFromMetadata(candidate.metadata) ?? candidate.level;
    return typeof maybeId === 'string' || typeof maybeId === 'number' || typeof maybeId === 'boolean' ? maybeId : rawDefaultValue;
}

function candidateIdFromMetadata(metadata: unknown): unknown {
    if (!metadata || typeof metadata !== 'object') {
        return undefined;
    }

    const record = metadata as Record<string, unknown>;
    return record.id ?? record.value ?? record.level;
}

export function toOptions(rawOptions: unknown): SearchFilterOption<SearchFilterValue>[] {
    if (!Array.isArray(rawOptions)) {
        return [];
    }

    return rawOptions
        .map((rawOption: MathiaFacetItem): SearchFilterOption<SearchFilterValue> | null => {
            const value = rawOption?.id ?? rawOption?.value;
            if (value === undefined || value === null) {
                return null;
            }

            return {
                value: String(value),
                label: String(rawOption.name ?? rawOption.label ?? value),
                metadata: rawOption,
            } satisfies SearchFilterOption<SearchFilterValue>;
        })
        .filter((option): option is SearchFilterOption<SearchFilterValue> => option !== null);
}
